import React, {
  Dispatch,
  SetStateAction,
  useState
} from 'react'
import {
  Dropdown,
  Icon,
  Menu,
  Responsive
} from 'semantic-ui-react'
import { Link } from 'react-router-dom'
import { connect } from 'react-redux'
import { push } from 'react-router-redux'
// Local
import {
  MESSAGE,
  PAGES,
  ROLES,
  TABLET_WIDTH
} from '../../constant'
import HeaderLogo from '../common/HeaderLogo'
import { hasRole } from '../common/roleUtils'
import { HeaderEncounterSearch } from '../encounter/SearchById'
import CdiCounter from '../cdi/cdiCounter'
import CoderCounter from '../coder/coderCounter'
import './header.css'
import { User } from './types'
import CoderEncounterModal from '../encounter/coder/CoderEncounterModal'
import { logout } from '../../action/user'
import { RootState } from '../../store'
import If from '../common/if'

type MenuLinks = {
  key: string
  link: string
  icon: string
  description: string
  validRoles: string[]
}

const menuLinks: MenuLinks[] = [
  {
    key: 'home',
    link: PAGES.HOME,
    icon: 'home',
    description: 'Home',
    validRoles: [
      ROLES.PHYSICIAN,
      ROLES.CDI,
      ROLES.CDI_RESTRICTED,
      ROLES.CLINICAL_ADMIN,
      ROLES.CODER,
      ROLES.CDI_QA_MANAGER,
      ROLES.CODER_QA_MANAGER
    ]
  },
  {
    key: 'patient',
    link: PAGES.PATIENT_LIST,
    icon: 'user outline',
    description: 'Patients',
    validRoles: [
      ROLES.PHYSICIAN,
      ROLES.CDI,
      ROLES.CDI_RESTRICTED,
      ROLES.CDI_QA_MANAGER,
      ROLES.CLINICAL_ADMIN
    ]
  },
  {
    key: 'encounter',
    link: PAGES.ENCOUNTER_LIST,
    icon: 'calendar alternate outline',
    description: 'Appointments',
    validRoles: [
      ROLES.CDI,
      ROLES.CDI_RESTRICTED,
      ROLES.CDI_QA_MANAGER,
      ROLES.CLINICAL_ADMIN,
      ROLES.CODER,
      ROLES.CODER_QA_MANAGER
    ]
  },
  {
    key: 'report',
    link: PAGES.REPORT,
    icon: 'list ol',
    description: 'Reports',
    validRoles: [
      ROLES.CDI,
      ROLES.CLINICAL_ADMIN,
      ROLES.CODER,
      ROLES.CDI_QA_MANAGER,
      ROLES.CODER_QA_MANAGER
    ]
  }
]

const mapStateToProps = (state: RootState) => ({
  currentUser: state.user,
  hasFormUpdates: state.coder.hasFormUpdates
})

type ReduxStateProps = ReturnType<typeof mapStateToProps>

type HeaderMenuLinkProps = {
  validRoles: string[]
  currentUser: User
  icon: any
  description: string
  link: keyof typeof PAGES
  active: string
  hasFormUpdates: boolean | undefined
  setActionState: Dispatch<SetStateAction<keyof typeof PAGES | undefined>>
}
export function HeaderMenuLink(
  props: HeaderMenuLinkProps
): JSX.Element {
  const {
    validRoles,
    currentUser,
    icon,
    description,
    link,
    active,
    hasFormUpdates,
    setActionState
  } = props

  if (
    hasRole(
      validRoles,
      currentUser.type as string
    )
  ) {
    const onMenuItemClick = (event: any) => {
      if (hasFormUpdates) {
        event.stopPropagation()
        event.preventDefault()
        setActionState(link)
      } else {
        setActionState(undefined)
        // Let the Link component handle navigation
      }
    }
    return (
      <Menu.Item
        onClick={ onMenuItemClick }
        as={ Link }
        to={ link }
        active={ active === link }
      >
        <Responsive
          // eslint-disable-next-line react/jsx-props-no-spreading
          { ...Responsive.onlyMobile }
          as={ Icon }
          name={ icon }
          fitted
        />
        <Responsive
          as="div"
          minWidth={ TABLET_WIDTH.minWidth }
        >
          { description }
        </Responsive>
      </Menu.Item>
    )
  }
  return <></>
}

type HeaderDropDownProps = {
  setActionState: Dispatch<SetStateAction<keyof typeof PAGES | undefined>>
  hasFormUpdates: boolean | undefined
  dispatch?: any
}

export function HeaderDropDown(props: HeaderDropDownProps): JSX.Element {
  const {
    setActionState,
    hasFormUpdates,
    dispatch
  } = props

  return (
    <Dropdown item icon="sidebar" pointing="top right">
      <Dropdown.Menu>
        <Dropdown.Item
          as={ Link }
          to={ PAGES.ACCOUNT }
          onClick={ (event: any) => {
            if (hasFormUpdates) {
              event.preventDefault()
              setActionState('ACCOUNT')
            }
          } }
        >
          Account
        </Dropdown.Item>
        <Dropdown.Item
          as={ Link }
          to={ PAGES.VERSION }
          onClick={ (event: any) => {
            if (hasFormUpdates) {
              event.preventDefault()
              setActionState('VERSION')
            }
          } }
        >
          Version
        </Dropdown.Item>
        <Dropdown.Divider />
        <Dropdown.Item
          onClick={ () => {
            logout(MESSAGE.LOGOUT, dispatch)
          } }
        >
          Logout
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  )
}

/**
 * @param active Name of active route (will match active header tab)
 * @param currentUser Current user
 */
type HeaderProps = ReduxStateProps & {
  active: any
  dispatch?: any
}

function isCdiWorker(role: keyof typeof ROLES): boolean {
  return role === ROLES.CDI || role === ROLES.CDI_RESTRICTED
}

function Header(
  props: HeaderProps
): JSX.Element {
  const {
    active,
    currentUser,
    hasFormUpdates,
    dispatch
  } = props
  const key = new Date().getTime()
  const [
    actionState,
    setActionState
  ] = useState<keyof typeof PAGES | undefined>(undefined)

  const goToPage = (page: keyof typeof PAGES) => {
    dispatch(push(PAGES[page]))
  }
  const handleLogoClick = (event: any) => {
    event.preventDefault()
    if (hasFormUpdates) {
      setActionState('HOME')
    } else {
      setActionState(undefined)
      dispatch(push(PAGES.HOME))
    }
  }
  return (
    <div id="curation-ui-header" key={ key }>
      <CoderEncounterModal
        onAction={ goToPage }
        actionState={ actionState }
        setActionState={ setActionState }
      />
      <Menu>
        <Menu.Item
          onClick={ handleLogoClick }
          as={ Link }
          to={ PAGES.HOME }
          id="header-logo"
        >
          <HeaderLogo />
        </Menu.Item>
        {
          menuLinks.map(hl => (
            <HeaderMenuLink
              key={ hl.key }
              link={ hl.link as keyof typeof PAGES }
              icon={ hl.icon }
              validRoles={ hl.validRoles }
              currentUser={ currentUser }
              description={ hl.description }
              active={ active }
              hasFormUpdates={ hasFormUpdates }
              setActionState={ setActionState }
            />
          ))
        }
        <Menu.Menu position="right">
          <HeaderEncounterSearch setActionState={ setActionState } />
          <If isTrue={ isCdiWorker(currentUser.type) }>
            <Menu.Item>
              <CdiCounter />
            </Menu.Item>
          </If>
          <If isTrue={ currentUser.type === ROLES.CODER }>
            <Menu.Item>
              <CoderCounter />
            </Menu.Item>
          </If>
          <Menu.Item>
            <Responsive
              minWidth={ TABLET_WIDTH.minWidth }
              className="current-user"
            >
              <If isTrue={ isCdiWorker(currentUser.type) }>
                <span>
                  <span>{ currentUser.username }</span>
                  <span> - </span>
                  <span>CDI</span>
                </span>
              </If>
              <If isTrue={ currentUser.type === ROLES.CODER }>
                <span>
                  <span>{ currentUser.username }</span>
                  <span> - </span>
                  <span>Coder</span>
                </span>
              </If>
              <If isTrue={ currentUser.type === ROLES.PHYSICIAN }>
                <span>
                  <span>{ currentUser.username }</span>
                  <span> - </span>
                  <span>Physician</span>
                </span>
              </If>
              <If isTrue={ currentUser.type === ROLES.CLINICAL_ADMIN }>
                <span>{ currentUser.username }</span>
              </If>
              <If isTrue={ currentUser.type === ROLES.ADMIN }>
                <span>
                  <span>{ currentUser.username }</span>
                  <span> - </span>
                  <span>Admin</span>
                </span>
              </If>
            </Responsive>
          </Menu.Item>
          <HeaderDropDown
            setActionState={ setActionState }
            hasFormUpdates={ hasFormUpdates }
            dispatch={ dispatch }
          />
        </Menu.Menu>
      </Menu>
    </div>
  )
}

export default connect(mapStateToProps)(Header)
